import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { api, formatApiError } from "@/lib/api";
import {
  FileText, HeartPulse, Loader2, Lock, LogOut, Save, Users, CheckCircle2, Inbox,
} from "lucide-react";
import { toast } from "sonner";
import AdminAppointments from "@/components/admin/AdminAppointments";

const STATUSES = ["Nuova", "In valutazione", "Contattata", "Supporto psicologico attivo", "Risolta"];

function LoginForm({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      onLogin(data);
    } catch (err) {
      setError(formatApiError(err));
    }
    setLoading(false);
  };

  return (
    <div className="container-site py-20 flex justify-center" data-testid="admin-login-page">
      <motion.form
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        onSubmit={submit}
        className="w-full max-w-md rounded-3xl border border-slate-100 bg-white shadow-xl shadow-brand-blue/5 p-8 sm:p-10"
        data-testid="admin-login-form"
      >
        <span className="w-14 h-14 rounded-2xl bg-brand-ink text-white flex items-center justify-center mx-auto">
          <Lock className="w-6 h-6" />
        </span>
        <h1 className="mt-5 text-2xl font-extrabold text-brand-ink text-center">Area riservata</h1>
        <p className="mt-2 text-center text-slate-500">Accesso operatori e amministratori</p>
        <div className="mt-8 space-y-5">
          <div>
            <label htmlFor="ad-email" className="label-field">Email</label>
            <input id="ad-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="input-field" data-testid="admin-email-input" autoComplete="username" />
          </div>
          <div>
            <label htmlFor="ad-password" className="label-field">Password</label>
            <input id="ad-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="input-field" data-testid="admin-password-input" autoComplete="current-password" />
          </div>
        </div>
        {error && (
          <p className="mt-4 rounded-xl bg-red-50 text-red-700 font-semibold px-4 py-3" role="alert" data-testid="admin-login-error">
            {error}
          </p>
        )}
        <button type="submit" disabled={loading} className="btn-primary w-full mt-7 disabled:opacity-60" data-testid="admin-login-button">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Lock className="w-5 h-5" />}
          {loading ? "Accesso…" : "Accedi"}
        </button>
      </motion.form>
    </div>
  );
}

function RequestCard({ req, onUpdated }) {
  const [status, setStatus] = useState(req.status);
  const [note, setNote] = useState(req.note || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/requests/${req.tracking_code}`, { status, note });
      toast.success(`Pratica ${req.tracking_code} aggiornata`);
      onUpdated();
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setSaving(false);
  };

  return (
    <article className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm" data-testid={`admin-request-card-${req.tracking_code}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-extrabold text-brand-ink">{req.tracking_code}</span>
        <span className="rounded-full bg-brand-mist text-brand-blue text-xs font-bold px-3 py-1">{req.status}</span>
        {req.urgenza === "Critico" && (
          <span className="inline-flex items-center gap-1 rounded-full bg-[#fbeae5] text-[#963b21] text-xs font-bold px-3 py-1">
            <HeartPulse className="w-3.5 h-3.5" /> Critico
          </span>
        )}
        <span className="ml-auto text-xs text-slate-400 font-semibold">
          {new Date(req.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" })}
        </span>
      </div>
      <p className="mt-3 font-bold text-brand-ink">{req.nome} {req.cognome} <span className="font-medium text-slate-500">— {req.citta}</span></p>
      <p className="text-sm text-slate-500">{req.email}{req.telefono ? ` · ${req.telefono}` : ""} · Debito: {req.fascia_debito}</p>
      <p className="mt-3 text-slate-600 leading-relaxed whitespace-pre-wrap">{req.descrizione}</p>
      <div className="mt-5 grid grid-cols-1 sm:grid-cols-[200px_1fr_auto] gap-3">
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="input-field h-12 text-base"
          data-testid={`admin-status-select-${req.tracking_code}`}
        >
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Nota interna…"
          className="input-field h-12 text-base"
          data-testid={`admin-note-input-${req.tracking_code}`}
        />
        <button onClick={save} disabled={saving} className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-aqua text-white font-bold px-6 h-12 disabled:opacity-60" data-testid={`admin-save-button-${req.tracking_code}`}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Salva
        </button>
      </div>
    </article>
  );
}

function Dashboard({ user, onLogout }) {
  const [stats, setStats] = useState(null);
  const [requests, setRequests] = useState([]);
  const [members, setMembers] = useState([]);
  const [tab, setTab] = useState("richieste");
  const [filter, setFilter] = useState("tutte");

  const load = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/requests").then((r) => setRequests(r.data)).catch(() => {});
    api.get("/admin/members").then((r) => setMembers(r.data)).catch(() => {});
  };

  useEffect(load, []);

  const filtered = useMemo(() => {
    if (filter === "critiche") return requests.filter((r) => r.urgenza === "Critico");
    if (filter === "aperte") return requests.filter((r) => r.status !== "Risolta");
    if (filter === "risolte") return requests.filter((r) => r.status === "Risolta");
    return requests;
  }, [requests, filter]);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    onLogout();
  };

  return (
    <div className="container-site py-12" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-brand-ink">Gestione pratiche</h1>
          <p className="text-slate-500">Benvenuto, {user.name} ({user.email})</p>
        </div>
        <button onClick={logout} className="ml-auto inline-flex items-center gap-2 rounded-full border-2 border-slate-200 px-5 h-11 font-bold text-brand-ink hover:bg-brand-mist transition-colors" data-testid="admin-logout-button">
          <LogOut className="w-4 h-4" /> Esci
        </button>
      </div>

      {stats && (
        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-stats">
          {[
            { icon: Inbox, label: "Richieste totali", value: stats.requests, color: "text-brand-blue" },
            { icon: Users, label: "Soci iscritti", value: stats.members, color: "text-brand-aqua" },
            { icon: HeartPulse, label: "Casi critici", value: stats.critical, color: "text-[#c24e2c]" },
            { icon: CheckCircle2, label: "Pratiche risolte", value: stats.resolved, color: "text-brand-aquadark" },
          ].map((s) => (
            <div key={s.label} className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm" data-testid={`admin-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <s.icon className={`w-6 h-6 ${s.color}`} />
              <p className="mt-3 text-3xl font-extrabold text-brand-ink">{s.value}</p>
              <p className="text-sm font-semibold text-slate-500">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      <div className="mt-10 flex flex-wrap items-center gap-2">
        <div className="grid grid-cols-3 gap-2 rounded-full bg-brand-mist p-1.5" data-testid="admin-tabs">
          <button onClick={() => setTab("richieste")} data-testid="admin-tab-richieste" className={`rounded-full px-5 h-10 font-bold text-sm transition-colors ${tab === "richieste" ? "bg-brand-blue text-white" : "text-brand-ink"}`}>
            Richieste ({requests.length})
          </button>
          <button onClick={() => setTab("iscritti")} data-testid="admin-tab-iscritti" className={`rounded-full px-5 h-10 font-bold text-sm transition-colors ${tab === "iscritti" ? "bg-brand-blue text-white" : "text-brand-ink"}`}>
            Iscritti ({members.length})
          </button>
          <button onClick={() => setTab("appuntamenti")} data-testid="admin-tab-appuntamenti" className={`rounded-full px-5 h-10 font-bold text-sm transition-colors ${tab === "appuntamenti" ? "bg-brand-blue text-white" : "text-brand-ink"}`}>
            Appuntamenti
          </button>
        </div>
        {tab === "richieste" && (
          <div className="flex gap-2 ml-auto" data-testid="admin-filters">
            {[["tutte", "Tutte"], ["critiche", "Critiche"], ["aperte", "Aperte"], ["risolte", "Risolte"]].map(([v, l]) => (
              <button key={v} onClick={() => setFilter(v)} data-testid={`admin-filter-${v}`} className={`rounded-full px-4 h-10 text-sm font-bold transition-colors ${filter === v ? "bg-brand-ink text-white" : "bg-brand-mist text-brand-ink hover:bg-slate-200"}`}>
                {l}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="mt-6 space-y-4">
        {tab === "appuntamenti" ? (
          <AdminAppointments />
        ) : tab === "richieste" ? (
          filtered.length === 0 ? (
            <p className="rounded-2xl bg-brand-mist p-8 text-center font-semibold text-slate-500" data-testid="admin-requests-empty">
              Nessuna pratica in questo filtro.
            </p>
          ) : (
            filtered.map((r) => <RequestCard key={r.tracking_code} req={r} onUpdated={load} />)
          )
        ) : members.length === 0 ? (
          <p className="rounded-2xl bg-brand-mist p-8 text-center font-semibold text-slate-500" data-testid="admin-members-empty">
            Nessun iscritto al momento.
          </p>
        ) : (
          <div className="rounded-2xl border border-slate-100 bg-white shadow-sm overflow-x-auto" data-testid="admin-members-table">
            <table className="w-full text-left min-w-[560px]">
              <thead>
                <tr className="border-b border-slate-100 text-sm text-slate-500 uppercase tracking-wider">
                  <th className="px-6 py-4 font-bold">Nome</th>
                  <th className="px-6 py-4 font-bold">Email</th>
                  <th className="px-6 py-4 font-bold">Città</th>
                  <th className="px-6 py-4 font-bold">Data</th>
                </tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <tr key={m.email} className="border-b border-slate-50 last:border-0" data-testid={`admin-member-row-${m.email}`}>
                    <td className="px-6 py-4 font-bold text-brand-ink">{m.nome} {m.cognome}</td>
                    <td className="px-6 py-4 text-slate-600">{m.email}</td>
                    <td className="px-6 py-4 text-slate-600">{m.citta}</td>
                    <td className="px-6 py-4 text-slate-500 text-sm">
                      {new Date(m.created_at).toLocaleDateString("it-IT")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(undefined);

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(null));
  }, []);

  if (user === undefined) {
    return (
      <div className="container-site py-24 flex justify-center" data-testid="admin-loading">
        <Loader2 className="w-8 h-8 animate-spin text-brand-blue" />
      </div>
    );
  }

  return user ? (
    <Dashboard user={user} onLogout={() => setUser(null)} />
  ) : (
    <LoginForm onLogin={setUser} />
  );
}
